import { useCallback, useMemo } from "react"

import { useScopedLocalStorage } from "@/workspaces/scope"

import type { MetadataColumnType } from "../utils/metadataColumnType"

export interface AddedMetadataColumn {
  key: string
  type: MetadataColumnType
}

interface MetadataColumnState {
  overrides: Record<string, MetadataColumnType>
  hidden: string[]
  added: AddedMetadataColumn[]
}

const EMPTY_STATE: MetadataColumnState = {
  overrides: {},
  hidden: [],
  added: [],
}

// The `testSetId || "new"` fallback means an unsaved, brand-new test set
// shares one storage slot until it's saved — useScopedLocalStorage further
// scopes that by org + workspace, or two orgs drafting a new test set in the
// same browser would read and overwrite each other's column overrides.
function storageKeyBase(testSetId: string): string {
  return `testSet.metadataColumns.${testSetId || "new"}`
}

export interface MetadataColumnStateApi {
  overrides: Record<string, MetadataColumnType>
  hiddenKeys: Set<string>
  addedColumns: AddedMetadataColumn[]
  setOverride: (key: string, type: MetadataColumnType) => void
  clearOverride: (key: string) => void
  hideColumn: (key: string) => void
  showColumn: (key: string) => void
  addColumn: (key: string, type: MetadataColumnType) => void
  removeAddedColumn: (key: string) => void
}

export function useMetadataColumnState(
  testSetId: string,
): MetadataColumnStateApi {
  const [state, setState] = useScopedLocalStorage<MetadataColumnState>(
    storageKeyBase(testSetId),
    EMPTY_STATE,
  )

  const hiddenKeys = useMemo(() => new Set(state.hidden ?? []), [state.hidden])

  // Each change is an update function, so one made before the scope resolves
  // replays on top of the stored state instead of replacing it.
  const setOverride = useCallback(
    (key: string, type: MetadataColumnType) => {
      setState((previous) => ({
        ...previous,
        overrides: { ...previous.overrides, [key]: type },
      }))
    },
    [setState],
  )

  const clearOverride = useCallback(
    (key: string) => {
      setState((previous) => {
        if (!(key in previous.overrides)) {
          return previous
        }
        const next = { ...previous.overrides }
        delete next[key]
        return { ...previous, overrides: next }
      })
    },
    [setState],
  )

  const hideColumn = useCallback(
    (key: string) => {
      setState((previous) => {
        const hidden = previous.hidden ?? []
        return hidden.includes(key)
          ? previous
          : { ...previous, hidden: [...hidden, key] }
      })
    },
    [setState],
  )

  const showColumn = useCallback(
    (key: string) => {
      setState((previous) => {
        const hidden = previous.hidden ?? []
        return hidden.includes(key)
          ? { ...previous, hidden: hidden.filter((k) => k !== key) }
          : previous
      })
    },
    [setState],
  )

  const addColumn = useCallback(
    (key: string, type: MetadataColumnType) => {
      const trimmed = key.trim()
      if (!trimmed) {
        return
      }
      setState((previous) => {
        const existing = previous.added ?? []
        return existing.some((c) => c.key === trimmed)
          ? previous
          : { ...previous, added: [...existing, { key: trimmed, type }] }
      })
    },
    [setState],
  )

  const removeAddedColumn = useCallback(
    (key: string) => {
      setState((previous) => {
        const existing = previous.added ?? []
        return existing.some((c) => c.key === key)
          ? { ...previous, added: existing.filter((c) => c.key !== key) }
          : previous
      })
    },
    [setState],
  )

  return {
    overrides: state.overrides,
    hiddenKeys,
    addedColumns: state.added ?? [],
    setOverride,
    clearOverride,
    hideColumn,
    showColumn,
    addColumn,
    removeAddedColumn,
  }
}
